import { endOfMonth, startOfMonth } from 'date-fns';
import { formatNumber } from './utils';

// Helper: guruhdan coursePrice topish
const getCoursePriceFromGroups = (groups, payment) => {
  const group = groups?.find((g) => g.id === payment.course);
  return Number(group?.coursePrice || payment.amount || 0);
};

export const getOutstandingPayments = (students) => {
  return students.filter(
    (student) => !hasPaidThisMonth(student.paymentHistory)
  );
};

export const getPaymentMethodBreakdown = (paymentHistory, groups) => {
  return paymentHistory.reduce((acc, payment) => {
    const method = payment.method;
    const amount = getCoursePriceFromGroups(groups, payment);

    if (acc[method]) {
      acc[method] += amount;
    } else {
      acc[method] = amount;
    }

    return acc;
  }, {});
};

export const getCoursePayments = (paymentHistory, groups) => {
  return paymentHistory.reduce((acc, payment) => {
    const group = groups.find((g) => g.id === payment.course);

    const course = group?.groupName || payment.course;

    const amount = getCoursePriceFromGroups(groups, payment);

    if (acc[course]) {
      acc[course] += amount;
    } else {
      acc[course] = amount;
    }

    return acc;
  }, {});
};

export const getTotalAmountPaid = (paymentHistory) => {
  return formatNumber(
    paymentHistory.reduce((total, payment) => total + Number(payment.amount), 0)
  );
};

export const getSelectedMonthAndYear = (selectedDate) => {
  const date = new Date(selectedDate);
  return {
    month: date.getMonth(),
    year: date.getFullYear(),
  };
};

export const getPaymentsForSelectedMonth = (
  paymentHistory,
  selectedMonth,
  selectedYear
) => {
  return paymentHistory.filter((payment) => {
    const paymentDate = new Date(payment.timestamp.seconds * 1000);
    const paymentMonth = paymentDate.getMonth();
    const paymentYear = paymentDate.getFullYear();
    return paymentMonth === selectedMonth && paymentYear === selectedYear;
  });
};

export const getMostPaidCourse = (paymentHistory, groups) => {
  // Aggregate payments by course
  const coursePayments = paymentHistory.reduce((acc, payment) => {
    const course = payment.course;
    const amount = getCoursePriceFromGroups(groups, payment);

    if (acc[course]) {
      acc[course] += amount;
    } else {
      acc[course] = amount;
    }

    return acc;
  }, {});

  // Convert the coursePayments object to an array of [course, totalAmount]
  const coursesWithTotalAmount = Object.entries(coursePayments).map(
    ([course, totalAmount]) => ({
      course,
      totalAmount,
    })
  );

  // Sort by total amount in descending order
  coursesWithTotalAmount.sort((a, b) => b.totalAmount - a.totalAmount);

  // Return the course with the highest total payment, or null if empty
  return coursesWithTotalAmount.length > 0 ? coursesWithTotalAmount[0] : null;
};

// Calculate student monthly Debt
export const calculateCurrentMonthDebt = (student, courses, groups) => {
  if (!student.paymentHistory || student.paymentHistory.length === 0) {
    return {
      coursePrice: 0,
      totalPaidThisMonth: 0,
      debt: 0,
    };
  }

  const currentMonthStart = startOfMonth(new Date());
  const currentMonthEnd = endOfMonth(new Date());

  // Safely get the course ID
  const firstPayment = student.paymentHistory[0];
  const courseId = groups.find((c) => c.id === firstPayment.course)?.courseId;
  const coursePrice = courses.find((c) => c.id === courseId)?.coursePrice || 0;

  // Filter payments made in the current month
  const currentMonthPayments = student.paymentHistory.filter((payment) => {
    const paymentDate = payment.timestamp?.seconds
      ? new Date(payment.timestamp.seconds * 1000)
      : null;
    return (
      paymentDate &&
      paymentDate >= currentMonthStart &&
      paymentDate <= currentMonthEnd
    );
  });

  // Calculate total amount paid in the current month
  const totalPaidThisMonth = currentMonthPayments.reduce(
    (sum, payment) => sum + Number(payment.amount),
    0
  );

  // Calculate debt if total paid is less than course price
  const debt =
    coursePrice > totalPaidThisMonth ? coursePrice - totalPaidThisMonth : 0;

  return {
    coursePrice,
    totalPaidThisMonth,
    debt,
  };
};

export function calculateMultiCoursePaymentsByMonth(
  student,
  courses,
  groups,
  selectedMonth = new Date()
) {
  const payments = Array.isArray(student?.paymentHistory)
    ? student.paymentHistory
    : [];

  const monthStart = startOfMonth(selectedMonth);
  const monthEnd = endOfMonth(selectedMonth);

  // Student qaysi guruhlarga tegishli ekanligini aniqlaymiz
  const studentGroups = (groups || []).filter(
    (g) => Array.isArray(g.students) && g.students.includes(student?.id)
  );

  const totalCourses = studentGroups.length;

  let paidCourses = 0;
  let totalPaidThisMonth = 0;
  let totalDebt = 0;

  const courseStatuses = studentGroups.map((group) => {
    // Guruhga bog‘liq kursni topamiz
    const course = (courses || []).find((c) => c.id === group.courseId);

    // Kurs nomini olish: birinchi o‘rinda groupName, keyin courseTitle
    const courseTitle =
      group?.groupName ||
      course?.courseTitle ||
      course?.title ||
      'Unknown course';

    // Kurs narxi: birinchi o‘rinda group.coursePrice, keyin kurs ichidan
    const coursePrice = Number(
      group?.coursePrice ??
        course?.coursePrice ??
        course?.price ??
        course?.fee ??
        0
    );

    // Shu guruh uchun o‘sha oyda to‘langan summalar
    const paymentsThisGroupThisMonth = payments.filter((p) => {
      let paymentDate = null;
      if (p?.timestamp?.seconds) {
        paymentDate = new Date(p.timestamp.seconds * 1000);
      } else if (typeof p?.timestamp === 'number') {
        paymentDate = new Date(p.timestamp);
      } else if (p?.createdAt) {
        paymentDate = new Date(p.createdAt);
      }

      if (!paymentDate) return false;

      const inMonth = paymentDate >= monthStart && paymentDate <= monthEnd;
      const sameGroup = p.course === group.id;
      return inMonth && sameGroup;
    });

    const paidAmount = paymentsThisGroupThisMonth.reduce(
      (s, p) => s + Number(p.amount || 0),
      0
    );

    const debt = Math.max(coursePrice - paidAmount, 0);

    if (paidAmount >= coursePrice && coursePrice > 0) {
      paidCourses += 1;
    }

    totalPaidThisMonth += paidAmount;
    totalDebt += debt;

    return {
      groupId: group.id,
      courseId: group.courseId,
      courseTitle,
      coursePrice,
      paidAmount,
      debt,
      status:
        coursePrice === 0
          ? 'no_price'
          : paidAmount >= coursePrice
          ? 'paid'
          : paidAmount > 0
          ? 'partial'
          : 'unpaid',
      rawCourseObject: course ?? null,
      paymentsThisGroupThisMonth,
    };
  });

  const overallStatus =
    paidCourses === totalCourses && totalCourses > 0
      ? 'paid'
      : paidCourses > 0 || totalPaidThisMonth > 0
      ? 'partial'
      : 'unpaid';

  return {
    courseStatuses,
    totalCourses,
    paidCourses,
    totalPaidThisMonth,
    totalDebt,
    overallStatus,
  };
}
